import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { ShieldCheck, Loader2, Store, Calendar, MapPin, UserPlus, Check } from "lucide-react";
import { apiClient } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { useAuth } from "../lib/auth";
import { localizeValue } from "../lib/displayValues";
import VehicleCard from "../components/VehicleCard";

export default function SellerPublicPage() {
  const { id } = useParams();
  const { lang } = useI18n();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const tr = (s) => localizeValue(s, lang);

  useEffect(() => {
    let alive = true;
    apiClient
      .get(`/public/seller/${id}`)
      .then(({ data }) => alive && setData(data))
      .catch((e) => alive && setErr(e.response?.data?.detail || e.message));
    return () => { alive = false; };
  }, [id]);

  if (err) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-16 text-center" data-testid="seller-page-error">
        <p className="text-[#6b6b6b]">
          {lang === "ar" ? "لم يتم العثور على البائع." : "Seller not found."}
        </p>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-24 flex items-center justify-center text-[#6b6b6b]" data-testid="seller-page-loading">
        <Loader2 className="w-5 h-5 animate-spin me-2" />
        {lang === "ar" ? "جاري التحميل…" : "Loading…"}
      </div>
    );
  }

  const initials = (data.name || "?")
    .split(/\s+/)
    .slice(0, 2)
    .map((s) => s[0]?.toUpperCase() || "")
    .join("");

  const joined = data.created_at
    ? new Date(data.created_at).toLocaleDateString(lang === "ar" ? "ar-SA" : "en-GB", {
        year: "numeric",
        month: "long",
      })
    : null;

  const isBusiness = data.kyc_type === "business";
  const profileUrl = typeof window !== "undefined" ? window.location.href : "";
  // iter330 — QR is a distinct traffic source per Analytics spec. The
  // seller-profile QR must attribute scans as `qr`, not `direct`.
  const qrUrl = typeof window !== "undefined"
    ? `${window.location.origin}${window.location.pathname}?utm_source=qr&utm_medium=qrcode&utm_campaign=seller_qr`
    : "";

  return (
    <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-10 py-10" data-testid="seller-public-page">
      {/* ── Hero: seller identity + QR (uncluttered, single card) ── */}
      <div
        className="rounded-3xl overflow-hidden border relative"
        style={{
          background: "linear-gradient(135deg,#0a0a0a 0%,#1a1611 100%)",
          borderColor: "rgba(201,165,92,0.35)",
        }}
      >
        <div className="grid sm:grid-cols-[1fr_auto] gap-6 p-6 sm:p-8 items-center">
          <div className="flex items-center gap-5">
            <div
              className="w-16 h-16 sm:w-20 sm:h-20 rounded-full flex items-center justify-center shrink-0 font-display text-xl sm:text-2xl font-bold"
              style={{
                background: "linear-gradient(135deg,#c9a55c,#8a6f2e)",
                color: "#0a0a0a",
              }}
              data-testid="seller-avatar"
            >
              {initials || <Store className="w-6 h-6" />}
            </div>
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-[0.25em] font-bold" style={{ color: "#c9a55c" }}>
                {isBusiness
                  ? (lang === "ar" ? "بائع تجاري" : "Business seller")
                  : (lang === "ar" ? "بائع فردي" : "Private seller")}
              </div>
              <h1 className="font-display text-2xl sm:text-3xl font-medium tracking-tight text-white truncate" data-testid="seller-name">
                {data.name}
              </h1>
              <div className="mt-2 flex flex-wrap items-center gap-3 text-xs text-[#e8e1cf]">
                {data.verified && (
                  <span
                    className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-bold"
                    style={{ backgroundColor: "rgba(46,160,67,0.18)", color: "#4ade80", border: "1px solid rgba(74,222,128,0.35)" }}
                    data-testid="seller-verified"
                  >
                    <ShieldCheck className="w-3.5 h-3.5" />
                    {lang === "ar" ? "موثّق" : "Verified"}
                  </span>
                )}
                {/* iter308 — Follow button. Only rendered when the
                    viewer is NOT the seller themselves. Guests get
                    routed to the /join chooser on click. */}
                {(!user || user.id !== data.id) && (
                  <FollowButton sellerId={data.id} lang={lang} user={user} navigate={navigate} />
                )}
                {data.city && (
                  <span className="inline-flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {tr(data.city)}</span>
                )}
                {joined && (
                  <span className="inline-flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> {lang === "ar" ? `عضو منذ ${joined}` : `Joined ${joined}`}</span>
                )}
                <span className="inline-flex items-center gap-1"><Store className="w-3.5 h-3.5" /> {lang === "ar" ? `${data.listings_count} إعلان` : `${data.listings_count} listing${data.listings_count === 1 ? "" : "s"}`}</span>
              </div>
            </div>
          </div>

          {/* QR — compact, printable, doesn't dominate the screen */}
          <div
            className="rounded-2xl p-3 flex items-center gap-3 self-center shrink-0"
            style={{ backgroundColor: "#ffffff" }}
            data-testid="seller-qr-panel"
          >
            <QRCodeSVG
              value={qrUrl}
              size={72}
              level="M"
              includeMargin={false}
              bgColor="#ffffff"
              fgColor="#0a0a0a"
            />
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#8a6f2e] leading-tight max-w-[8rem]">
              {lang === "ar" ? "امسح لمشاركة صفحتي" : "Scan to share my page"}
            </div>
          </div>
        </div>
      </div>

      {/* ── Listings grid — single, quiet section ── */}
      <div className="mt-10">
        <div className="flex items-baseline justify-between mb-5">
          <h2 className="font-display text-xl font-medium tracking-tight text-[#0a0a0a]">
            {lang === "ar" ? "إعلانات هذا البائع" : "This seller's listings"}
          </h2>
          <span className="text-xs text-[#8a8a8a]">
            {lang === "ar" ? `${data.vehicles.length} إعلان` : `${data.vehicles.length} item${data.vehicles.length === 1 ? "" : "s"}`}
          </span>
        </div>

        {data.vehicles.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-[#0a0a0a]/15 py-16 text-center text-[#8a8a8a]" data-testid="seller-empty">
            {lang === "ar" ? "لا توجد إعلانات نشطة حالياً." : "No active listings yet."}
          </div>
        ) : (
          <div className="columns-1 sm:columns-2 lg:columns-3 gap-6 [&>*]:break-inside-avoid [&>*]:mb-6" data-testid="seller-listings-grid">
            {data.vehicles.map((v) => (
              <VehicleCard key={v.id} v={v} />
            ))}
          </div>
        )}
      </div>

      <div className="mt-10 text-center">
        <Link to="/marketplace" className="text-sm text-[#8a6f2e] hover:text-[#c9a55c]">
          ← {lang === "ar" ? "العودة للسوق" : "Back to marketplace"}
        </Link>
      </div>
    </div>
  );
}

/* iter308 — FollowButton
   ─────────────────────────────────────────────────────────
   Renders next to the "موثّق" chip. Two visual states:
     • Idle  → hollow chip, gold-outline border
     • Following → gold-filled pill (matches CARTAL identity)
   Errors surface as inline text in #B42318 (professional
   error red) — no toasts, no page-level disruption.        */
function FollowButton({ sellerId, lang, user, navigate }) {
  const [status, setStatus] = React.useState({ following: false, loaded: false });
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState("");

  React.useEffect(() => {
    let alive = true;
    apiClient
      .get(`/users/${sellerId}/follow-status`)
      .then(({ data }) => {
        if (alive) setStatus({ following: !!data.following, loaded: true });
      })
      .catch(() => { if (alive) setStatus({ following: false, loaded: true }); });
    return () => { alive = false; };
  }, [sellerId]);

  const onClick = async () => {
    if (busy) return;
    if (!user) { navigate("/join"); return; }
    setError("");
    setBusy(true);
    const wasFollowing = status.following;
    try {
      if (wasFollowing) {
        await apiClient.delete(`/users/${sellerId}/follow`);
        setStatus({ following: false, loaded: true });
      } else {
        await apiClient.post(`/users/${sellerId}/follow`);
        setStatus({ following: true, loaded: true });
      }
    } catch (e) {
      setError(lang === "ar" ? "تعذر تنفيذ العملية، حاول مرة أخرى." : "Action failed, try again.");
    } finally {
      setBusy(false);
    }
  };

  // Hidden until we know the true state to avoid a UI flicker.
  if (!status.loaded) return null;

  const label = status.following
    ? (lang === "ar" ? "تتابعه" : "Following")
    : (lang === "ar" ? "متابعة" : "Follow");

  return (
    <>
      <button
        type="button"
        onClick={onClick}
        disabled={busy}
        aria-busy={busy}
        aria-pressed={status.following}
        data-testid="seller-follow-btn"
        className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-bold transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-offset-transparent"
        style={
          status.following
            ? {
                background: "linear-gradient(135deg,#e8c873,#c9a55c)",
                color: "#0a0a0a",
                border: "1px solid rgba(201,165,92,0.9)",
                boxShadow: "0 4px 14px -6px rgba(201,165,92,0.8)",
                opacity: busy ? 0.7 : 1,
              }
            : {
                background: "rgba(201,165,92,0.08)",
                color: "#e8c873",
                border: "1px solid rgba(201,165,92,0.55)",
                opacity: busy ? 0.7 : 1,
              }
        }
      >
        {busy ? (
          <Loader2 className="w-3.5 h-3.5 animate-spin" />
        ) : status.following ? (
          <Check className="w-3.5 h-3.5" strokeWidth={2.6} />
        ) : (
          <UserPlus className="w-3.5 h-3.5" strokeWidth={2.4} />
        )}
        <span>{label}</span>
      </button>
      {error && (
        <span
          role="alert"
          className="text-[11px] font-semibold"
          style={{ color: "#B42318" }}
          data-testid="seller-follow-error"
        >
          {error}
        </span>
      )}
    </>
  );
}
